import * as THREE from 'three';
import type { BattleRules, BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The blasting (La Quemadura 4, docs/QUEMADURA.md): the first Vigil's watchtower where an extraction's node would be,
 * Tobin's smoke at its top and the dark beacon over it; the cracks round the clearing's rim the nightmares climb out
 * of, red from inside and breathing haze; the powder barrels: stacked by the tower, carried on a hero's shoulder,
 * dropped, planted at a crack with the fuse spitting. A blast at a crack narrows it (dimmer, smaller); the last one
 * shuts it to a smoking scar. All shut, the beacon is lit: a great flame and a warm halo at the top.
 *
 * Draw calls: the tower (one merged mesh), its flame, one mesh per crack's glowing gap and one for all their lips, the
 * barrels (one instanced mesh), a glow layer. No lights.
 */

type Blast = NonNullable<BattleRules['blast']>;

const STONE = ['#1e1c20', '#252228', '#1a181c', '#2a2630'];
const MORTAR = '#121014';
const EMBER = new THREE.Color('#ff4f3a');
const WARM = new THREE.Color('#ffb070');
const HOT = new THREE.Color('#fff0d8');
const HAZE = new THREE.Color('#ff6a3a');
const TALL = 3.4;
const MAX_BARRELS = 10;

/** The powder keg's parts into a kit: dark staves, iron hoops, a black band, a stub of fuse; standing, middle at `m`. */
const kegParts = (kit: Kit, m: THREE.Matrix4): void => {
  kit.add(new THREE.CylinderGeometry(0.2, 0.2, 0.5, 10), '#4e3a2c', m);
  kit.add(new THREE.CylinderGeometry(0.23, 0.23, 0.28, 10), '#5a4232', m);
  for (const y of [-0.19, 0.19]) kit.add(new THREE.CylinderGeometry(0.235, 0.235, 0.035, 10), '#3a3c40', m, place(0, y, 0));
  kit.add(new THREE.CylinderGeometry(0.236, 0.236, 0.06, 10), '#1a1416', m, place(0, 0, 0));
  kit.add(new THREE.CylinderGeometry(0.17, 0.17, 0.012, 10), '#3a2a20', m, place(0, 0.255, 0));
  kit.add(new THREE.CylinderGeometry(0.015, 0.015, 0.12, 4), '#c8b8a0', m, place(0.05, 0.31, 0, 0, 1, 0, 0.3));
};

/** One keg, standing, its middle at the origin (the barrels in play are instances of it). */
const kegGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  kegParts(kit, new THREE.Matrix4());
  const mesh = kit.build(new THREE.MeshBasicMaterial(), 'keg') as THREE.Mesh;
  (mesh.material as THREE.Material).dispose();
  return mesh.geometry;
};

export class BlastLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private kegs: THREE.InstancedMesh;
  private flame: THREE.Mesh;
  private flameMat: THREE.MeshBasicMaterial;
  private cracks: { mesh: THREE.Mesh; mat: THREE.MeshStandardMaterial; at: THREE.Vector3; yaw: number; size: number }[] = [];
  private tower: THREE.Vector3;
  private top: THREE.Vector3;
  private stackAt: THREE.Vector3;
  private now = 0;
  private s: BattleState | null = null;
  /** When the beacon was lit (s, frame clock), how open each crack looks (eased toward its state), the tower's last life. */
  private litAt = -1;
  private open: number[] = [];
  private keepHp = -1;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private one = new THREE.Vector3(1, 1, 1);
  private small = new THREE.Vector3(0.72, 0.72, 0.72);
  /** How far a keg's blast hurts, in world units (the rules' `radius`, as the sim's `boom` reads it). */
  private reach: number;

  constructor(
    private ctx: OmenCtx,
    private r: Blast,
    heroPid: (pid: string) => number | undefined,
  ) {
    this.heroOf = heroPid;
    this.reach = (r.radius / 1000) * WORLD_PER_HEX;
    this.group.name = 'blast';
    const g0 = ctx.ground(0, 0);
    this.tower = new THREE.Vector3(0, g0, 0);
    this.top = new THREE.Vector3(0, g0 + TALL + 0.5, 0);
    this.stackAt = ctx.at(r.stack.x, r.stack.y, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);

    // The tower: a stepped plinth sunk in the glass, a tapering shaft of black stone courses, a gallery of crenels,
    // the beacon's iron cup on top; the Vigil's open eye carved over its door; a slit window, soot streaks.
    const body = new Kit();
    const base = place(0, g0 - 0.1, 0, Math.PI / 6);
    const stone = (i: number): string => STONE[i % STONE.length] as string;
    body.add(new THREE.CylinderGeometry(1.15, 1.3, 0.35, 8), stone(0), base, place(0, 0.17, 0));
    body.add(new THREE.CylinderGeometry(1.0, 1.1, 0.3, 8), stone(1), base, place(0, 0.45, 0));
    const courses = 9;
    for (let i = 0; i < courses; i++) {
      const t = i / courses;
      const rad = 0.72 - t * 0.16;
      const h = (TALL - 1.1) / courses;
      body.add(new THREE.CylinderGeometry(rad - 0.02, rad, h * 0.92, 8), stone(i + 2), base, place(0, 0.6 + h * (i + 0.5), 0, (i % 2) * 0.12));
      body.add(new THREE.CylinderGeometry(rad + 0.005, rad + 0.005, h * 0.08, 8), MORTAR, base, place(0, 0.6 + h * (i + 1), 0));
    }
    // The gallery: a wider ring and eight crenels.
    const gy = TALL - 0.5;
    body.add(new THREE.CylinderGeometry(0.76, 0.6, 0.26, 8), stone(1), base, place(0, gy, 0));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      body.add(box, stone(i), base, place(Math.sin(a) * 0.68, gy + 0.28, Math.cos(a) * 0.68, a, new THREE.Vector3(0.24, 0.3, 0.14)));
    }
    // The beacon's cup: iron on four legs over the gallery, a ring of black bars.
    body.add(new THREE.CylinderGeometry(0.5, 0.3, 0.3, 8), '#2e2b2b', base, place(0, TALL + 0.05, 0));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.2;
      body.add(box, '#3a3634', base, place(Math.sin(a) * 0.46, TALL + 0.28, Math.cos(a) * 0.46, a, new THREE.Vector3(0.05, 0.3, 0.05), 0.25));
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      body.add(box, '#2e2b2b', base, place(Math.sin(a) * 0.32, TALL - 0.2, Math.cos(a) * 0.32, a, new THREE.Vector3(0.06, 0.5, 0.06), 0, -0.3));
    }
    // Charred wood in the cup (what Tobin lit), dark until the beacon takes.
    for (let i = 0; i < 5; i++) body.add(box, '#1a1210', base, place(Math.cos(i * 1.3) * 0.15, TALL + 0.22, Math.sin(i * 1.3) * 0.15, i, new THREE.Vector3(0.07, 0.07, 0.42), 0.4));
    // The door: a dark arch at the foot, the open eye carved in the lintel over it (pale stone, a red iris).
    const door = place(0, 0, 0.73, 0);
    const dm = new THREE.Matrix4().multiplyMatrices(base, door);
    body.add(box, '#0a080a', dm, place(0, 1.0, 0.02, 0, new THREE.Vector3(0.5, 0.9, 0.06)));
    body.add(new THREE.CylinderGeometry(0.25, 0.25, 0.06, 10, 1, false, 0, Math.PI), '#0a080a', dm, place(0, 1.45, 0.02, 0, 1, Math.PI / 2, Math.PI / 2));
    const eye = new THREE.Shape();
    eye.moveTo(-0.24, 0);
    eye.quadraticCurveTo(0, 0.17, 0.24, 0);
    eye.quadraticCurveTo(0, -0.17, -0.24, 0);
    body.add(new THREE.ShapeGeometry(eye, 8), '#a8a29a', dm, place(0, 1.95, 0.05));
    body.add(new THREE.CircleGeometry(0.075, 12), '#c43a30', dm, place(0, 1.95, 0.055));
    body.add(new THREE.CircleGeometry(0.032, 8), '#121014', dm, place(0, 1.95, 0.06));
    // A slit window higher up, and soot running down from the top.
    body.add(box, '#0a080a', base, place(0, 2.35, 0.66, 0, new THREE.Vector3(0.1, 0.38, 0.06)));
    for (const [a, h] of [[0.6, 1.0], [2.4, 0.7], [4.1, 1.2]] as const) body.add(box, '#0c0a0c', base, place(Math.sin(a) * 0.62, TALL - 0.7 - h / 2, Math.cos(a) * 0.62, a, new THREE.Vector3(0.18, h, 0.02)));
    // The glass crept up its plinth: dark glossy lumps.
    const lump = new THREE.IcosahedronGeometry(0.5, 0);
    for (let i = 0; i < 7; i++) {
      const a = i * 0.9 + 0.3;
      const s = 0.35 + (i % 3) * 0.12;
      body.add(lump, '#0e0b0d', base, place(Math.sin(a) * 1.25, 0.05, Math.cos(a) * 1.25, a, new THREE.Vector3(s, s * 0.5, s)));
    }
    // The powder stack by the tower: kegs leaning on each other, a plank, an empty one on its side.
    const sg = ctx.ground(this.stackAt.x, this.stackAt.z);
    const sy = Math.atan2(this.stackAt.x, this.stackAt.z);
    const sm = place(this.stackAt.x, sg, this.stackAt.z, sy);
    for (const [x, y, z, tilt] of [[-0.42, 0.26, -0.3, 0], [0.42, 0.26, -0.32, 0.05], [0, 0.26, -0.55, -0.04], [-0.2, 0.72, -0.42, 0.08], [0.6, 0.12, 0.3, Math.PI / 2]] as const) kegParts(body, new THREE.Matrix4().multiplyMatrices(sm, place(x, y, z, x * 2, 1, 0, tilt)));
    body.add(box, '#3a2e26', sm, place(0, 0.04, 0.05, 0.3, new THREE.Vector3(1.2, 0.06, 0.32)));
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.06, flatShading: true });
    this.mats.push(mat);
    const tower = body.build(mat, 'blast-tower');
    if (tower) {
      tower.castShadow = !ctx.low;
      tower.receiveShadow = true;
      this.geos.push(tower.geometry);
      this.group.add(tower);
    }

    // The beacon's flame: tongues of fire in the cup, hidden until lit.
    const fk = new Kit();
    const id = new THREE.Matrix4();
    fk.add(new THREE.ConeGeometry(0.42, 1.3, 7), '#ff8a3a', id, place(0, 0.65, 0));
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      fk.add(new THREE.ConeGeometry(0.18, 0.8, 5), i % 2 ? '#ffb070' : '#ff5a2a', id, place(Math.sin(a) * 0.24, 0.42, Math.cos(a) * 0.24, a, 1, Math.sin(a) * 0.25, Math.cos(a) * 0.25));
    }
    fk.add(new THREE.ConeGeometry(0.2, 0.9, 6), '#fff0d8', id, place(0, 0.5, 0));
    this.flameMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.92, depthWrite: false });
    this.mats.push(this.flameMat);
    this.flame = fk.build(this.flameMat, 'blast-flame') as THREE.Mesh;
    this.geos.push(this.flame.geometry);
    this.flame.matrixAutoUpdate = false;
    this.flame.visible = false;
    this.group.add(this.flame);

    // The cracks: a jagged rift in the glass, red from inside (each its own material so it can dim and close), between
    // dark lifted lips of glass (all cracks' lips in one still mesh).
    const lips = new Kit();
    const lip = new THREE.IcosahedronGeometry(0.3, 0);
    r.cracks.forEach((c, i) => {
      const at = ctx.at(c.x, c.y, 0);
      const cg = ctx.ground(at.x, at.z);
      // Along the rim: the rift runs across the line to the tower.
      const yaw = Math.atan2(at.x, at.z) + Math.PI / 2;
      const lm = place(at.x, cg, at.z, yaw);
      const ck = new Kit();
      const n = 7;
      for (let k = 0; k < n; k++) {
        const t = k / (n - 1) - 0.5;
        const w = 0.22 * (1 - Math.abs(t) * 1.5) + 0.05;
        const x = t * 2.1;
        const z = Math.sin(k * 2.1 + i) * 0.12;
        // The glowing gap (its own mesh), then the dark lifted lips either side.
        ck.add(box, k % 2 ? '#ff6a2a' : '#ff3a1a', id, place(x, 0.0, z, Math.sin(k * 1.7) * 0.3, new THREE.Vector3(0.4, 0.05, w)));
        for (const s of [-1, 1]) lips.add(lip, k % 2 ? '#0c0a0c' : '#141016', lm, place(x + Math.sin(k * 3.1) * 0.08, 0.02, z + s * (w * 0.5 + 0.13), k + s, new THREE.Vector3(0.62, 0.26 - Math.abs(t) * 0.12, 0.36), s * 0.35));
      }
      const cm = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, flatShading: true, emissive: '#ff3a1a', emissiveIntensity: 1 });
      this.mats.push(cm);
      const mesh = ck.build(cm, `blast-crack-${i}`) as THREE.Mesh;
      this.geos.push(mesh.geometry);
      mesh.matrixAutoUpdate = false;
      this.group.add(mesh);
      this.cracks.push({ mesh, mat: cm, at: at.setY(cg), yaw, size: 1 });
      this.open.push(1);
    });
    const lipMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.25, metalness: 0.35, flatShading: true });
    this.mats.push(lipMat);
    const lipMesh = lips.build(lipMat, 'blast-lips');
    if (lipMesh) {
      lipMesh.receiveShadow = true;
      this.geos.push(lipMesh.geometry);
      this.group.add(lipMesh);
    }

    // The barrels in play: on the stack, carried, dropped, planted.
    const kmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.mats.push(kmat);
    const keg = kegGeo();
    this.geos.push(keg);
    this.kegs = new THREE.InstancedMesh(keg, kmat, MAX_BARRELS);
    this.kegs.count = 0;
    this.kegs.castShadow = !ctx.low;
    this.kegs.frustumCulled = false;
    this.group.add(this.kegs);
    this.group.add(this.glow.mesh);
    this.place();
  }

  private heroOf: (pid: string) => number | undefined;

  /** Every fresh sim tick: the events (blasts, the beacon), the tower's hurt, haze from open cracks, fuses spitting. */
  tick(s: BattleState): void {
    this.s = s;
    const low = this.ctx.low;
    const st = s.blast;
    // The tower hurt: dust off its stones; badly hurt, smoke and embers from its cracks.
    if (this.keepHp >= 0 && s.keep.hp < this.keepHp) {
      const a = s.tick * 2.39;
      this.ctx.vfx.burst(this.v.set(Math.sin(a) * 0.8, this.tower.y + 0.8 + (s.tick % 5) * 0.5, Math.cos(a) * 0.8), { n: low ? 2 : 4, color: '#4a4448', speed: 0.02, up: 0.01, size: 0.18, life: 14, smoke: true, spread: 0.3 });
    }
    this.keepHp = s.keep.hp;
    const hurt = s.keep.maxHp ? 1 - s.keep.hp / s.keep.maxHp : 0;
    if (hurt > 0.5 && s.tick % (low ? 10 : 5) === 0) this.ctx.vfx.burst(this.v.set(0.4, this.tower.y + 2.2, 0.5), { n: 1, color: '#2a2224', speed: 0.004, up: 0.025, size: 0.4, life: 26, smoke: true, spread: 0.25 });
    // Tobin's fire: a thread of smoke from the cup until the beacon takes.
    if (!st?.lit && s.tick % (low ? 6 : 3) === 0) this.ctx.vfx.burst(this.top.clone().setY(this.top.y - 0.1), { n: 1, color: '#5a5052', speed: 0.004, up: 0.03, size: 0.35, life: 34, smoke: true, spread: 0.15 });
    // Lit: sparks rising off the flame.
    if (st?.lit && s.tick % 2 === 0) this.ctx.vfx.burst(this.top.clone().setY(this.top.y + 0.9), { n: low ? 1 : 3, color: '#ffb070', speed: 0.015, up: 0.05, size: 0.05, life: 22, gravity: -0.002, spread: 0.35 });
    // Open cracks breathe red haze; shut ones, a thin smoke.
    this.cracks.forEach((c, i) => {
      const sealed = !!st?.sealed[i];
      if (s.tick % (low ? 14 : 7) !== i % 7) return;
      if (sealed) this.ctx.vfx.burst(c.at.clone().setY(c.at.y + 0.1), { n: 1, color: '#3a3234', speed: 0.004, up: 0.02, size: 0.35, life: 28, smoke: true, spread: 0.5 });
      else this.ctx.vfx.burst(c.at.clone().setY(c.at.y + 0.15), { n: low ? 1 : 2, color: '#7a3a2a', speed: 0.006, up: 0.018, size: 0.55, life: 30, smoke: true, spread: 0.9 });
    });
    for (const b of st?.barrels ?? []) {
      // A planted fuse spits sparks; a battered keg smokes.
      if (b.fuse) {
        this.ctx.vfx.burst(this.ctx.at(b.x, b.y, 0.6), { n: low ? 2 : 4, color: '#ffd080', speed: 0.03, up: 0.04, size: 0.04, life: 10, gravity: 0.004, spread: 0.3 });
        // While it burns, the blast's real reach on the ground: a red line at the radius that blinks faster as the
        // fuse runs down, and a second one running out to it. Rings of the shared Vfx pool, no mesh or material.
        const k = Math.max(0, Math.min(1, 1 - (b.fuse - s.tick) / Math.max(1, this.r.fuse)));
        const g = this.ctx.at(b.x, b.y, 0.05);
        const blink = s.tick % (k > 0.6 ? 2 : 4) < (k > 0.6 ? 1 : 2) ? 0.3 : 0;
        this.ctx.vfx.ring(g, this.reach * 0.97, this.reach, '#ff4f3a', 2, 0.55 + blink);
        const c = Math.max(0.1, this.reach * k);
        this.ctx.vfx.ring(g, c * 0.97, c, '#ff8a6e', 2, 0.4 + 0.4 * k);
      } else if (b.hp < this.r.hp * 0.5 && s.tick % 4 === 0) this.ctx.vfx.burst(this.kegAt(b, this.v).clone(), { n: 1, color: '#4a4448', speed: 0.004, up: 0.02, size: 0.22, life: 18, smoke: true, spread: 0.15 });
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'blast') continue;
      const p = this.ctx.at(e.x, e.y, 0.3);
      if (e.kind === 'ready') this.ctx.vfx.burst(this.stackAt.clone().setY(this.stackAt.y + 0.3), { n: low ? 3 : 6, color: '#8a8282', speed: 0.015, up: 0.02, size: 0.25, life: 16, smoke: true, spread: 0.4 });
      else if (e.kind === 'lift') this.ctx.vfx.burst(p, { n: low ? 2 : 5, color: '#6a6466', speed: 0.015, up: 0.015, size: 0.2, life: 12, smoke: true, spread: 0.3 });
      else if (e.kind === 'plant') {
        this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#ffd080', speed: 0.04, up: 0.05, size: 0.05, life: 14, gravity: 0.004, spread: 0.3 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.2, this.reach, '#ff8a6e', 12, 0.7);
      } else if (e.kind === 'crack' || e.kind === 'seal' || e.kind === 'burst') this.boom(p, e.kind === 'seal');
      else if (e.kind === 'beacon') {
        this.litAt = this.now;
        this.ctx.flare(this.top.clone().setY(this.top.y + 0.6), '#fff0d8', 3.6, 28);
        this.ctx.flare(this.top.clone().setY(this.top.y + 0.6), '#ff8a3a', 6, 36);
        this.ctx.vfx.burst(this.top.clone().setY(this.top.y + 0.6), { n: low ? 14 : 36, color: '#ffb070', speed: 0.07, up: 0.08, size: 0.07, life: 34, gravity: -0.001, spread: 0.6 });
        this.ctx.vfx.ring(this.tower.clone().setY(this.tower.y + 0.1), 0.6, 5.5, '#ffb070', 24, 0.6);
        this.ctx.shake(0.18);
      }
    }
  }

  /** A keg going off: a flash, fire and smoke, black glass flying, the ground shaking; the last one at a crack, bigger. */
  private boom(p: THREE.Vector3, big: boolean): void {
    const low = this.ctx.low;
    this.ctx.flare(p.clone().setY(p.y + 0.4), '#fff0d8', big ? 2.8 : 2.2, 10);
    // The glow's billboard no wider than the blast (it fades to nothing at its edge).
    this.ctx.flare(p.clone().setY(p.y + 0.3), '#ff6a2a', Math.min(big ? 4.4 : 3.4, this.reach * 2), 18);
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 8 : 20, color: '#ff8a3a', speed: 0.09, up: 0.07, size: 0.08, life: 18, gravity: 0.004, spread: 0.6 });
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 5 : 12, color: '#3a3234', speed: 0.035, up: 0.03, size: 0.55, life: 40, smoke: true, spread: 0.9 });
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: '#0e0b0d', speed: 0.1, up: 0.09, size: 0.09, life: 24, gravity: 0.01, spread: 0.6 });
    // The wave ends at the blast's real radius (it went out to 2.8–3.6 where the blast hurts to 2.16).
    this.ctx.vfx.ring(p.clone().setY(p.y - 0.25), 0.3, this.reach, '#ff8a6e', 16, 0.75);
    this.ctx.shake(big ? 0.4 : 0.3);
  }

  /** Where a keg is drawn: on its carrier's shoulder (as drawn this frame), else where it lies. */
  private kegAt(b: NonNullable<BattleState['blast']>['barrels'][number], out: THREE.Vector3): THREE.Vector3 {
    if (b.pid) {
      const id = this.heroOf(b.pid);
      const p = id !== undefined ? this.ctx.pos(id) : undefined;
      if (p) return out.copy(p).setY(p.y + 0.95);
    }
    return this.ctx.at(b.x, b.y, 0.26).clone();
  }

  /** Every redraw: the barrels, the cracks (eased to how open they are), the beacon, the glows. */
  draw(): void {
    this.place();
    const st = this.s?.blast;
    this.glow.begin();
    // Barrels.
    let n = 0;
    for (const b of st?.barrels ?? []) {
      if (n >= MAX_BARRELS) break;
      const p = this.kegAt(b, this.v);
      const carried = !!b.pid;
      // Carried on the shoulder, tipped; planted, leaning into the crack; else standing.
      this.q.setFromEuler(new THREE.Euler(carried ? 0.5 : b.fuse ? 0.35 : 0, b.id * 1.3, carried ? 1.2 : 0, 'YXZ'));
      // A little smaller on a shoulder, so it doesn't swallow the hero carrying it.
      this.m.compose(p, this.q, carried ? this.small : this.one);
      this.kegs.setMatrixAt(n++, this.m);
      if (b.fuse) this.glow.add(p.x, p.y + 0.35, p.z, WARM, 0.7, 0.55 + 0.25 * Math.sin(this.now * 30 + b.id));
      else if (!carried && b.crack < 0 && (b.x - this.r.stack.x) ** 2 + (b.y - this.r.stack.y) ** 2 < this.r.reach ** 2) this.glow.add(p.x, p.y + 0.1, p.z, EMBER, 1.0, 0.18 + 0.06 * Math.sin(this.now * 3));
    }
    this.kegs.count = n;
    this.kegs.instanceMatrix.needsUpdate = true;
    // Cracks: red glow over the open ones, pulsing.
    this.cracks.forEach((c, i) => {
      const o = this.open[i] ?? 0;
      if (o > 0.05) this.glow.add(c.at.x, c.at.y + 0.2, c.at.z, HAZE, 1.7 * (0.5 + o * 0.5), (0.16 + 0.06 * Math.sin(this.now * 2.2 + i)) * o);
    });
    // The beacon: a warm halo, bigger the first moments.
    if (this.litAt >= 0) {
      const since = this.now - this.litAt;
      const swell = since < 1.2 ? 1 + (1.2 - since) * 1.6 : 1;
      const flick = 0.85 + 0.15 * Math.sin(this.now * 11) * Math.sin(this.now * 7.3);
      this.glow.add(this.top.x, this.top.y + 0.7, this.top.z, WARM, 2.6 * swell, 0.45 * flick);
      this.glow.add(this.top.x, this.top.y + 0.6, this.top.z, HOT, 1.1 * swell, 0.6 * flick);
      this.glow.add(this.top.x, this.top.y + 0.4, this.top.z, EMBER, 4.2 * swell, 0.1);
    } else this.glow.add(this.top.x, this.top.y - 0.15, this.top.z, EMBER, 0.6, 0.14 + 0.06 * Math.sin(this.now * 4));
    this.glow.end();
  }

  private place(): void {
    const st = this.s?.blast;
    // Each crack eased toward how open it is: full, narrowed after a blast, shut.
    this.cracks.forEach((c, i) => {
      const target = st?.sealed[i] ? 0 : (st?.blasts[i] ?? 0) > 0 ? 0.5 : 1;
      const o = (this.open[i] ?? 1) + (target - (this.open[i] ?? 1)) * 0.08;
      this.open[i] = o;
      c.mat.emissiveIntensity = 0.14 + o * 0.86;
      this.q.setFromEuler(new THREE.Euler(0, c.yaw, 0));
      this.m.compose(this.v.copy(c.at).setY(c.at.y - (1 - o) * 0.06), this.q, new THREE.Vector3(0.75 + o * 0.25, 1, 0.22 + o * 0.78));
      c.mesh.matrix.copy(this.m);
      c.mesh.matrixWorldNeedsUpdate = true;
    });
    // The flame: lit, it sways and breathes.
    this.flame.visible = this.litAt >= 0;
    if (this.flame.visible) {
      const since = this.now - this.litAt;
      const grow = Math.min(1, since / 0.5);
      const breathe = 1 + 0.08 * Math.sin(this.now * 9) + 0.05 * Math.sin(this.now * 13.7);
      this.q.setFromEuler(new THREE.Euler(0.06 * Math.sin(this.now * 3.1), this.now * 0.6, 0.06 * Math.sin(this.now * 2.3)));
      this.flame.matrix.compose(this.v.copy(this.top).setY(this.top.y - 0.25), this.q, new THREE.Vector3(1.1 * grow, 1.3 * grow * breathe, 1.1 * grow));
      this.flame.matrixWorldNeedsUpdate = true;
    }
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.kegs.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
